import { observer } from 'mobx-react-lite'
import { useObservableValue } from '@/hooks/rx-hooks'
import { useMemo } from 'react'
import { clock } from '@/utils/rx-util'
import { JobProgression } from '@/features/jobs/taxes/progression/JobProgression'
import { links } from '@/utils/link'
import type { ComputedProgressionStepContent } from '@/features/jobs/taxes/progression/progression-content'
import {
  getDisplayHeadlineForBookkeeping,
  getStatusDisplayAction,
  getStatusDisplayHeadline,
} from '@/features/jobs/job-status-display'
import { useNavigation } from 'react-router'
import { sum } from 'lodash-es'
import type { LegendServiceType } from '@/features/jobs/legends/legend-service-type'
import { match } from '@/utils/pattern-match-util'

import { SkeletonListInfiniteScrollTrigger } from '@/components/SkeletonListInfiniteScrollTrigger'
import {
  Jobs,
  JobSkeleton,
  DefaultJob,
  TaxJob,
  ConsultationJob,
  BookkeepingJob,
  UnderConstructionJob,
} from '@/features/dashboard/components/Jobs/index'
import type { Id } from '@/infra/id'
import type { DashboardViewModel } from '@/features/dashboard/DashboardView/DashboardViewModel'
import type { JobListItemViewModel } from '@/features/jobs/list/JobListItemViewModel'

export interface JobsContainerProps {
  vm: DashboardViewModel
  isDisabled?: boolean
}

/**
 * The JobsContainer
 */
export const JobsContainer = observer(
  ({ vm, isDisabled }: JobsContainerProps) => {
    const now = useObservableValue(useMemo(() => clock('minute'), []))
    const navigation = useNavigation()

    // Helper function to check if a specific job is being navigated to
    const isNavigatingToJob = (jobId: Id) => {
      return Boolean(navigation.location?.pathname === links.jobDetails(jobId))
    }

    return (
      <Jobs>
        {vm.jobListVm.jobVms.map((jobVm) => {
          const job = jobVm.job
          const currentStepContent = job.currentStepContent
          const isNavigating = isNavigatingToJob(job.id)

          switch (true) {
            case job.status === 'UnderConstruction':
              return (
                <UnderConstructionJobContainer
                  key={job.id}
                  vm={jobVm}
                  now={now}
                  onArchive={() => jobVm.promptDelete()}
                  isNavigating={isNavigating}
                  isDisabled={isDisabled}
                />
              )

            case job.legend.serviceType === 'Consultation':
              return (
                <ConsultationJobContainer
                  key={job.id}
                  vm={jobVm}
                  now={now}
                  isNavigating={isNavigating}
                  isDisabled={isDisabled}
                />
              )

            case job.legend.serviceType === 'Bookkeeping':
              return (
                <BookkeepingJobContainer
                  key={job.id}
                  vm={jobVm}
                  now={now}
                  isNavigating={isNavigating}
                  isDisabled={isDisabled}
                />
              )

            case job.progression !== JobProgression.none &&
              !!currentStepContent:
              return (
                <TaxJobContainer
                  key={job.id}
                  vm={jobVm}
                  currentStepContent={currentStepContent}
                  now={now}
                  isNavigating={isNavigating}
                  isDisabled={isDisabled}
                />
              )

            default:
              return (
                <DefaultJobContainer
                  key={job.id}
                  vm={jobVm}
                  now={now}
                  isNavigating={isNavigating}
                  isDisabled={isDisabled}
                />
              )
          }
        })}

        <SkeletonListInfiniteScrollTrigger
          scroller={vm.jobListVm.scroller}
          maxSkeletons={5}
          attachRefTo="all"
        >
          <JobSkeleton />
        </SkeletonListInfiniteScrollTrigger>
      </Jobs>
    )
  },
)

export interface TaxJobContainerProps {
  vm: JobListItemViewModel
  currentStepContent: ComputedProgressionStepContent
  now: Date
  isNavigating: boolean
  isDisabled?: boolean
}

/**
 * The TaxJobContainer
 */
export const TaxJobContainer = observer(
  ({
    vm,
    currentStepContent,
    now,
    isNavigating,
    isDisabled,
  }: TaxJobContainerProps) => {
    const { job, unreadCount } = vm
    return (
      <TaxJob
        id={job.id}
        title={getStatusDisplayHeadline({
          status: job.status,
          progression: {
            flow: job.progression.flow,
            step: job.progression.current,
          },
        })}
        subtitle={job.displayName}
        actionText={getStatusDisplayAction({
          status: job.status,
          progression: {
            flow: job.progression.flow,
            step: job.progression.current,
          },
        })}
        badgeCount={unreadCount}
        serviceTypeLabel={formatServiceType(job.legend.serviceType)}
        updatedAt={job.lastActivityAt}
        now={now}
        progress={
          job.status === 'Closed'
            ? 100
            : Math.max(currentStepContent.progress.completed, 5)
        }
        href={isDisabled ? undefined : links.jobDetails(job.id)}
        isPending={isNavigating}
        isDisabled={isDisabled}
        testId={jobItemTestId(job.id)}
      />
    )
  },
)

export interface ConsultationJobContainerProps {
  vm: JobListItemViewModel
  now: Date
  isNavigating: boolean
  isDisabled?: boolean
}

/**
 * The ConsultationJobContainer
 */
export const ConsultationJobContainer = observer(
  ({ vm, now, isNavigating, isDisabled }: ConsultationJobContainerProps) => {
    const { job, unreadCount } = vm
    const title = job.championPro
      ? `Consultation with ${job.championPro?.user.displayName}`
      : 'Consultation'

    return (
      <ConsultationJob
        id={job.id}
        title={title}
        subtitle={job.displayName}
        badgeCount={unreadCount}
        serviceTypeLabel={formatServiceType(job.legend.serviceType)}
        href={isDisabled ? undefined : links.jobDetails(job.id)}
        startTime={job.consultation.bookedTime?.startTime}
        isPendingCall={job.consultation.status === 'PendingCall'}
        updatedAt={job.lastActivityAt}
        now={now}
        isPending={isNavigating}
        isDisabled={isDisabled}
        testId={jobItemTestId(job.id)}
      />
    )
  },
)

export interface BookkeepingJobContainerProps {
  vm: JobListItemViewModel
  now: Date
  isNavigating: boolean
  isDisabled?: boolean
}

/**
 * The BookkeepingJobContainer
 */
export const BookkeepingJobContainer = observer(
  ({ vm, now, isNavigating, isDisabled }: BookkeepingJobContainerProps) => {
    const { job, unreadCount } = vm
    const completedCount = sum(
      job.bookkeepingProgressionInfo.workItems.map((x) => x.completedCount),
    )
    const total = sum(
      job.bookkeepingProgressionInfo.workItems.map((x) => x.quantity),
    )

    return (
      <BookkeepingJob
        id={job.id}
        title={getDisplayHeadlineForBookkeeping(
          job.bookkeepingProgressionInfo?.displayStage,
        )}
        subtitle={job.displayName}
        badgeCount={unreadCount}
        serviceTypeLabel={formatServiceType(job.legend.serviceType)}
        href={isDisabled ? undefined : links.jobDetails(job.id)}
        updatedAt={job.lastActivityAt}
        now={now}
        total={total}
        completedCount={completedCount}
        isPending={isNavigating}
        isDisabled={isDisabled}
        testId={jobItemTestId(job.id)}
      />
    )
  },
)

export interface UnderConstructionJobContainerProps {
  vm: JobListItemViewModel
  now: Date
  onArchive: () => void
  isNavigating: boolean
  isDisabled?: boolean
}

/**
 * The UnderConstructionJobContainer
 */
export const UnderConstructionJobContainer = observer(
  ({
    vm,
    now,
    onArchive,
    isNavigating,
    isDisabled,
  }: UnderConstructionJobContainerProps) => {
    const { job, unreadCount } = vm
    return (
      <UnderConstructionJob
        id={job.id}
        title="Your answers are needed to continue"
        subtitle={job.displayName}
        badgeCount={unreadCount}
        serviceTypeLabel={formatServiceType(job.legend.serviceType)}
        href={isDisabled ? undefined : links.jobDetails(job.id)}
        updatedAt={job.lastActivityAt}
        now={now}
        onArchive={isDisabled ? undefined : onArchive}
        isPending={isNavigating}
        isDisabled={isDisabled}
        testId={jobItemTestId(job.id)}
      />
    )
  },
)

export interface DefaultJobContainerProps {
  vm: JobListItemViewModel
  now: Date
  isNavigating: boolean
  isDisabled?: boolean
}

/**
 * The DefaultJobContainer
 */
export const DefaultJobContainer = observer(
  ({ vm, now, isNavigating, isDisabled }: DefaultJobContainerProps) => {
    const { job, unreadCount } = vm
    return (
      <DefaultJob
        id={job.id}
        title={job.displayName}
        subtitle={job.legend.name}
        badgeCount={unreadCount}
        serviceTypeLabel={formatServiceType(job.legend.serviceType)}
        href={isDisabled ? undefined : links.jobDetails(job.id)}
        updatedAt={job.lastActivityAt}
        now={now}
        isPending={isNavigating}
        isDisabled={isDisabled}
        testId={jobItemTestId(job.id)}
      />
    )
  },
)

/**
 * Format Service Type
 */
function formatServiceType(serviceType: LegendServiceType) {
  return match(serviceType, {
    Tax: 'Tax full service',
    Consultation: 'Advisory',
    Bookkeeping: 'Bookkeeping',
    Misc: 'Misc',
  })
}

/**
 * Gets the test ID for the job item.
 *
 * @param id
 */
function jobItemTestId(id: Id) {
  return `job-item-${id}`
}
